import { useContext, useState } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import { withRouter } from '../../../../utils/WithRouter';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import {
  Button,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  TextField,
  Grid,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  CircularProgress,
} from '@mui/material';

import TextInput from '../../../common/formControls/TextInput';
import PageTitle from '../../../common/PageTitle';
import { CreateDealContext } from '../../../../context/CreateDealContext';
import { IMerge } from '../../../../types/merge';
import { IUserArray, IUser } from '../../../../types/user';
import Dropdown from '../../../common/formControls/Dropdown';
import { IExecutor } from '../../../../types/project';
import { getMergeValues, pageRotate, wrapText } from '../../../../utils/Functions';
import { validateMultipleEmails } from '../../../../utils/Validations';
import { FlexBetween } from '../../../../commonStyles';
import { useSelector } from 'react-redux';
import { selectProject } from '../../../../features/project/projectSlice';
import { selectMerges } from '../../../../features/merge/mergeSlice';
import { useAppDispatch } from '../../../../app/hooks';
import { showErrorSnackbar } from '../../../../features/snackbar/snackbarSlice';
import { useMergeQuery } from '../../../../features/merge/mergeHooks';
import { mergeType } from '../../../../utils/Constants';
import UploadForm from './UploadForm';
import CreateForm from './CreateForm';

const MergeStep = (props: any) => {
  const {
    purchaserInfo,
    realtorInfo,
    suiteInfo,
    setSuiteInfo,
    setActiveStep,
    activeStep,
    selectedUnit,
    setSelectedUnit,
    setApsPdf,
    setTotalPdf,
    purchaserId,
    selectedMergeTemplates,
    setSelectedMergeTemplates,
    uploadNewDeal,
    urlType,
    setValidEmails,
    setAutoDocuments,
    executor,
    setExecutor,
    autoDocuments,
    setPdfBlob,
    selectedAps,
    rental,
    setPdfTemplate,
  } = useContext(CreateDealContext);
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  useMergeQuery(project._id, mergeType, 0, 50000, "", selectedUnit);
  const merges = useSelector(selectMerges);

  const [users, setUsers] = useState<IUser[]>([]);
  const [cc, setCc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useQuery<IUserArray>(GETSALES, {
    variables: { filter: { OR: [{ type: 'Sales' }, { type: 'Manager' }], locked: false }, sort: 'NAME' },
    onCompleted: (data) => {
      setUsers(data.userMany);
    },
  });

  const [savePdf] = useMutation(SAVEPDF, {
    onCompleted: (data) => {
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleTextInput = async (event: any) => {
    setSuiteInfo({
      ...suiteInfo,
      [event.target.id]: event.target.value,
    });
  };

  const handleSalesDropdown = (event: React.ChangeEvent<{ name?: string; value: string }>) => {
    let selectedUser = users.find((user: IUser) => user.fullName === event.target.value);
    setSuiteInfo({
      ...suiteInfo,
      salesRep: selectedUser,
    });
  };

  const modifyPdf = async () => {
    setLoading(true)
    let validEmails: string[] = [];
    if (project._id === '62df06402e0f41a93c3cd81a') {
      if (!selectedMergeTemplates.length) {
        storeDispatch(showErrorSnackbar('YOU NEED A SCHEDULE C'));
        return;
      }
    }
    if (cc) {
      validEmails = validateMultipleEmails(cc);
      if (validEmails.length === 0) {
        storeDispatch(showErrorSnackbar('There is an error with email formatting'));
      }
      setValidEmails(validEmails);
    }
    if (!selectedUnit.floorPlan) {
      storeDispatch(showErrorSnackbar('There is no Floorplan Template for this unit.'));
      return;
    }
    if (selectedUnit.floorPlan.signFields.length === 0) {
      storeDispatch(showErrorSnackbar('There are no sign fields for the floorplan'));
      return;
    }
    if (!suiteInfo.salesRep) {
      storeDispatch(showErrorSnackbar('Please add a Sales Representative to the deal'));
      return;
    }
    if (project.acknowledgement && !executor) {
      storeDispatch(showErrorSnackbar('Please add an Executor'));
      return;
    }

    // The floorplan and warranty sheet are each inserted after their own APS page number. The server (savePdf) shifts
    // the APS sign fields past each one by its template's totalPages and places their own sign fields.
    let floorplanMerge = project.mergeTemplates.find((merge: any) => merge.name === 'floorplan');
    let floorplanAPS = floorplanMerge?.apsTemplates.find(
      (aps: any) => selectedAps.name === aps.name || selectedAps._id === aps.apsTemplate._id
    );
    let warrantyMerge = project.mergeTemplates.find((merge: any) => merge.name === 'warranty information sheet');
    let warrantyAPS = warrantyMerge?.apsTemplates.find(
      (aps: any) => selectedAps.name === aps.name || selectedAps._id === aps.apsTemplate._id
    );
    const includeFloorplan = !!floorplanAPS && floorplanAPS.pageNumber !== 0;
    const includeWarranty = !!warrantyAPS?.pageNumber;

    // Without the warranty page the APS sign fields would land one page off, so don't generate without it
    if (includeWarranty && (!selectedUnit.warrantySheet || !selectedUnit.getWarrantyUrl)) {
      storeDispatch(showErrorSnackbar('There is no Warranty Sheet Template for this unit.'));
      setLoading(false);
      return;
    }

    let arrayOfPdfs: Array<any> = [];
    const mergedPdf = await PDFDocument.create();

    // Draw APS first

    let aps = await fetch(selectedAps!.getUrl!).then(async (res) => await res.arrayBuffer());
    const apsDoc = await PDFDocument.load(aps);
    let helveticaFont = await apsDoc.embedFont(StandardFonts.Helvetica);

    let pages = apsDoc.getPages();
    await selectedAps.mergeFields.forEach(async (mergeField: any) => {
      let mergePages = pages[mergeField.pageNumber];
      let mergeValue = getMergeValues(
        mergeField,
        project,
        { ...selectedUnit, rental: rental ? rental : selectedUnit.rental },
        null,
        purchaserInfo,
        [],
        [],
        suiteInfo.deposit,
        suiteInfo.options,
        realtorInfo,
        null,
        null,
        null,
        selectedMergeTemplates
      );

      let textProperties: any = {
        x: mergeField.x,
        y: mergePages.getHeight() - mergeField.y - 3,
        size: mergeField.fontSize,
        font: helveticaFont,
        color: rgb(0, 0, 0),
      };

      if (mergeField.wrap) {
        mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, helveticaFont, 9);
        if (mergeValue.lineHeight) {
          textProperties.lineHeight = mergeValue.lineHeight;
          textProperties.y = textProperties.y + 10;
        }
        mergeValue = mergeValue.text;
      }

      mergePages.drawText(mergeValue, textProperties);
    });

    let copiedPages = await mergedPdf.copyPages(apsDoc, apsDoc.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));

    // Draw Floorplan and Warranty Sheet After, each at its own APS page number

    const drawUnitDocument = async (url: string, template: any) => {
      let unitPdf = await fetch(url).then(async (res) => await res.arrayBuffer());
      const unitDoc = await PDFDocument.load(unitPdf);
      const unitFont = await unitDoc.embedFont(StandardFonts.Helvetica);

      let unitPages = unitDoc.getPages();
      for (const mergeField of template.mergeFields) {
        let mergePages = unitPages[mergeField.pageNumber];

        let pageType = mergePages.getRotation();
  
        let pageRotationValue = pageRotate(pageType, mergeField.x, mergeField.y, 1, mergePages.getSize());
  
        let pageRotation = mergePages.getRotation();
        if (pageRotation.angle === 0) {
          pageRotationValue.y = mergePages.getHeight() - mergeField.y - 3;
        }
  
        let checkMediaBox = mergePages.getMediaBox();
        let x = pageRotationValue.x;
        let y = pageRotationValue.y;
  
        if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
          x = pageRotationValue.x - Math.abs(checkMediaBox.x);
          y = pageRotationValue.y - Math.abs(checkMediaBox.y);
        }
  
        let mergeValue = await getMergeValues(
          mergeField,
          project,
          { ...selectedUnit, rental: rental ? rental : selectedUnit.rental },
          null,
          purchaserInfo,
          [],
          [],
          suiteInfo.deposit,
          suiteInfo.options
        );

        let textProperties: any = {
          x: x,
          y: y,
          size: mergeField.fontSize,
          font: unitFont,
          color: rgb(0, 0, 0),
          rotate: mergePages.getRotation(),
        };

        if (mergeField.wrap) {
          mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, unitFont, 9);
          if (mergeValue.lineHeight) {
            textProperties.lineHeight = mergeValue.lineHeight;
            textProperties.y = textProperties.y + 10;
          }
          mergeValue = mergeValue.text;
        }

        mergePages.drawText(mergeValue, textProperties);
      }

      return unitDoc;
    };

    // Inserted unit documents with their APS page number and page count, used to shift the amendments below
    const insertedDocs: { pageNumber: number; pageCount: number }[] = [];
    const unitDocs: { doc: PDFDocument; pageNumber: number }[] = [];

    if (includeFloorplan) {
      unitDocs.push({ doc: await drawUnitDocument(selectedUnit.getUrl, selectedUnit.floorPlan), pageNumber: floorplanAPS.pageNumber });
    }
    if (includeWarranty) {
      unitDocs.push({
        doc: await drawUnitDocument(selectedUnit.getWarrantyUrl, selectedUnit.warrantySheet),
        pageNumber: warrantyAPS.pageNumber,
      });
    }

    // Insert from the highest page number down so earlier inserts don't move later ones. On the same page the
    // warranty is inserted first, which leaves the floorplan ahead of it.
    for (const unitDoc of [...unitDocs].reverse().sort((a, b) => b.pageNumber - a.pageNumber)) {
      copiedPages = await mergedPdf.copyPages(unitDoc.doc, unitDoc.doc.getPageIndices());
      copiedPages.forEach((page, index) => mergedPdf.insertPage(unitDoc.pageNumber + index, page));
      insertedDocs.push({ pageNumber: unitDoc.pageNumber, pageCount: copiedPages.length });
    }

    // Draw Amendments After

    let newSelectedMerges: any[] = [];

    if (selectedMergeTemplates.length > 0) {
      newSelectedMerges = selectedMergeTemplates
        .map((mergeTemplate: any) => {
          // get page number placement for selected merge template then sort from smallest to biggest
          let apsTemplate = project.mergeTemplates.find((merge: any) => mergeTemplate._id === merge._id);
          let pageNumbers = apsTemplate?.apsTemplates.find(
            (mergeTemplate: any) => mergeTemplate.name === selectedAps.name || mergeTemplate._id === selectedAps._id
          );
          return {
            ...mergeTemplate,
            page: pageNumbers?.pageNumber,
          };
        })
        .sort((a: any, b: any) => parseFloat(a.page) - parseFloat(b.page));
    }

    for (let file of newSelectedMerges) {
      let templateUrl = await fetch(file!.getUrl!).then(async (res) => await res.arrayBuffer());
      arrayOfPdfs.push(templateUrl);
    }

    for (let [index, document] of arrayOfPdfs.entries()) {
      document = await PDFDocument.load(document);
      const helveticaFont = await document.embedFont(StandardFonts.Helvetica);

      let pages = document.getPages();
      await newSelectedMerges[index].mergeFields.forEach(async (mergeField: any) => {
        let mergePages = pages[mergeField.pageNumber];

        let mergeValue = getMergeValues(
          mergeField,
          project,
          { ...selectedUnit, rental: rental ? rental : selectedUnit.rental },
          null,
          purchaserInfo,
          [],
          [],
          suiteInfo.deposit,
          suiteInfo.options,
          realtorInfo
        );

        let textProperties: any = {
          x: mergeField.x,
          y: mergePages.getHeight() - mergeField.y - 3,
          size: mergeField.fontSize,
          font: helveticaFont,
          color: rgb(0, 0, 0),
        };

        if (mergeField.wrap) {
          mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, helveticaFont, 9);
          if (mergeValue.lineHeight) {
            textProperties.lineHeight = mergeValue.lineHeight;
            textProperties.y = textProperties.y + 10;
          }
          mergeValue = mergeValue.text;
        }

        mergePages.drawText(mergeValue, textProperties);
      });

      let mergeTemplatePages = await mergedPdf.copyPages(document, document.getPageIndices());

      let mergeTemplate = await project.mergeTemplates.find(
        (merge: any) =>
          merge.name === newSelectedMerges[index].name || (merge.mergeTemplate && merge.mergeTemplate._id === newSelectedMerges[index]._id)
      );

      let mergeTemplateAPS = mergeTemplate?.apsTemplates.find(
        (aps: any) => selectedAps.name === aps.name || selectedAps._id === aps.apsTemplate._id
      );

      let addPages = 0;

      for (let i = 0; i < index; i++) {
        addPages += newSelectedMerges[i].totalPages;
      }
      
      for (const insertedDoc of insertedDocs) {
        if (mergeTemplateAPS?.pageNumber! >= insertedDoc.pageNumber) {
          addPages += insertedDoc.pageCount;
        }
      }

      newSelectedMerges[index].page = mergeTemplateAPS?.pageNumber! + addPages;

      mergeTemplatePages.reverse().forEach((merge: any, index) => {
        mergedPdf.insertPage(mergeTemplateAPS?.pageNumber! + addPages, mergeTemplatePages[index]);
      });
    }

    setSelectedMergeTemplates(newSelectedMerges);

    let pdfMergeDoc: any = await mergedPdf.save();
    await setApsPdf(pdfMergeDoc);
    await setPdfBlob(new Blob([pdfMergeDoc]));

    if (autoDocuments.length > 0) {
      let allDocuments = [];
      // Documents attached to the front go before every APS page, in the order they were listed.
      let frontPageCount = 0;

      for (const document of autoDocuments) {
        let autoMergeTemplate = merges.find((mergeTemplate: IMerge) => mergeTemplate.name === document.name);

          let autoDocument = await fetch(autoMergeTemplate?.getUrl!).then(async (res) => await res.arrayBuffer());
          const documentPdf = await PDFDocument.load(autoDocument);
          let helveticaFont = await documentPdf.embedFont(StandardFonts.Helvetica);

          let pages = documentPdf.getPages();
          await autoMergeTemplate?.mergeFields.forEach(async (mergeField: any) => {
            let mergePages = pages[mergeField.pageNumber];
            let mergeValue = getMergeValues(
              mergeField,
              project,
              { ...selectedUnit, rental: rental ? rental : selectedUnit.rental },
              null,
              purchaserInfo,
              [],
              [],
              suiteInfo.deposit,
              suiteInfo.options,
              realtorInfo,
              null,
              null,
              null,
              null
            );
  
            let pageType = mergePages.getRotation();
      
            let pageRotationValue = pageRotate(pageType, mergeField.x, mergeField.y, 1, mergePages.getSize());
      
            let pageRotation = mergePages.getRotation();
            if (pageRotation.angle === 0) {
              pageRotationValue.y = mergePages.getHeight() - mergeField.y - 3;
            }
      
            let checkMediaBox = mergePages.getMediaBox();
            let x = pageRotationValue.x;
            let y = pageRotationValue.y;
      
            if (checkMediaBox && (checkMediaBox.x !== 0 || checkMediaBox.y !== 0)) {
              x = pageRotationValue.x - Math.abs(checkMediaBox.x);
              y = pageRotationValue.y - Math.abs(checkMediaBox.y);
            }

            let textProperties: any = {
              x: x,
              y: y,
              size: mergeField.fontSize,
              font: helveticaFont,
              color: rgb(0, 0, 0),
              rotate: mergePages.getRotation(),
            };

            if (mergeField.wrap) {
              mergeValue = wrapText(mergeValue, mergeField.wrap * 2 + 75, helveticaFont, 9);
              if (mergeValue.lineHeight) {
                textProperties.lineHeight = mergeValue.lineHeight;
                textProperties.y = textProperties.y + 10;
              }
              mergeValue = mergeValue.text;
            }

            mergePages.drawText(mergeValue, textProperties);
          });

          let copiedPages = await mergedPdf.copyPages(documentPdf, documentPdf.getPageIndices());
          if (document.position === 'front') {
            copiedPages.forEach((page: any) => mergedPdf.insertPage(frontPageCount++, page));
          } else {
            copiedPages.forEach((page: any) => mergedPdf.addPage(page));
          }

          const documentDoc = await documentPdf.save();

          allDocuments.push({
            file: documentDoc,
            name: document.name,
            mergeTemplate: document.mergeTemplate,
            signFields: autoMergeTemplate?.signFields!,
            position: document.position,
          })
      }
      setAutoDocuments(allDocuments);
    }

    pdfMergeDoc = await mergedPdf.save();

    await setTotalPdf(pdfMergeDoc);

    const pdfBlob = new Blob([pdfMergeDoc], { type: 'application/pdf' });

    const file = new File([pdfBlob], 'APS-Preview', { type: 'application/pdf'})
    
    let token = await savePdf({
      variables: {
        project: project._id,
        unit: selectedUnit._id,
        file: file,
        purchaserIds: purchaserId,
        mergeTemplate: selectedAps._id,
        floorplan: floorplanAPS && floorplanAPS.pageNumber ? selectedUnit.floorPlan._id : null,
        warranty: includeWarranty ? selectedUnit.warrantySheet._id : null,
        additional: autoDocuments.filter((document: any) => document.position !== 'front').map((document: any) => document.mergeTemplate),
        front: autoDocuments.filter((document: any) => document.position === 'front').map((document: any) => document.mergeTemplate),
        executor: true,
        aps: true
      }
    })
    
    setPdfTemplate(token.data.savePdf)
    setLoading(false);
    setActiveStep(activeStep + 1);
  };
  
  return (
    <div>
      <PageTitle title={'Review'} />
      {urlType === 'upload-deal' ? <UploadForm /> : <CreateForm />}
      <h2>RDS Sales Representative</h2>
      <div>
        <Dropdown
          title={'Select A Sales Representative'}
          menuList={users.map((user: IUser) => user.fullName)}
          name={'options'}
          handleSelect={(e: any) => handleSalesDropdown(e)}
          value={suiteInfo.salesRep ? suiteInfo.salesRep.fullName : ''}
        />
      </div>
      {project.acknowledgement ? (
        <>
          <h2>Executor</h2>
          <div>
            <FormControl fullWidth>
              <InputLabel id="id-question-label">Executor</InputLabel>
              <Select
                value={executor}
                labelId="id-executor-label"
                id="id-executor"
                label="Executor"
                name="executor"
                onChange={(e) => setExecutor(e.target.value)}
              >
                {project.executors.map((executor: IExecutor) => {
                  return (
                    <MenuItem value={executor.email}>
                      {executor.name} - {executor.email}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
        </>
      ) : null}
      <h2>CC to additional email addresses</h2>
      <div>
        <TextInput
          id="cc"
          placeholder="Please add additional emails to cc. (ex, johndoe@gmail.com, test2@gmail.com)"
          value={cc}
          handleTextInput={(e: any) => setCc(e.target.value)}
        />
      </div>
      <h2>Miscellaneous</h2>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl>
            <FormLabel id="radio-buttons-group-label">Did the Sales Rep ID the purchasers?</FormLabel>
            <RadioGroup
              row
              name="access"
              value={suiteInfo.verifiedId}
              onChange={(e) =>
                setSuiteInfo({
                  ...suiteInfo,
                  verifiedId: e.target.value === 'true' ? true : false,
                })
              }
            >
              {['Yes', 'No'].map((key: any, index: number) => (
                <FormControlLabel value={key === 'Yes' ? true : false} key={index} control={<Radio color="primary" />} label={key} />
              ))}
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>
      {project._id === '65fc6ef32c574aea51c28500' || project._id === '646e965a0be1b1b0f611190d' ? (
        <>
          <h2>Number of Months of Free Maintenance</h2>
          <TextField
            title={'Number of Months'}
            name={'days'}
            fullWidth
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            value={selectedUnit.days}
            label={'Number of Months'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedUnit({ ...selectedUnit, days: e.target.value })}
          />
        </>
      ) : null}
      <h2>Comments</h2>
      <div>
        <TextInput
          id="comments"
          placeholder="Please enter any notes or comments for this deal if necessary"
          multiline
          variant={'outlined'}
          rows={2}
          value={suiteInfo.comments}
          rowsMax={Infinity}
          handleTextInput={(e: any) => handleTextInput(e)}
        />
      </div>
      <FlexBetween sx={{ mt: 1 }}>
        <Button variant="contained" color="primary" onClick={() => setActiveStep(activeStep - 1)}>
          Back
        </Button>
        {urlType === 'upload-deal' ? (
          <Button color="success" variant="contained" onClick={() => uploadNewDeal()}>
            Upload Deal
          </Button>
        ) : (
          <Button disabled={loading || !suiteInfo.salesRep} color="success" variant="contained" onClick={() => modifyPdf()}>
            {loading ? <CircularProgress size={16} style={{ color: "#fff" }} />  : "Generate APS"}
          </Button>
        )}
      </FlexBetween>
    </div>
  );
};

const GETSALES = gql`
  query userMany($filter: FilterFindManyUserInput, $sort: SortFindManyUserInput) {
    userMany(filter: $filter, sort: $sort) {
      _id
      fullName
    }
  }
`;

const SAVEPDF = gql`
  mutation savePdf($project: MongoID!, $unit: MongoID!, $file: Upload, $purchaserIds: [MongoID], $mergeTemplate: MongoID, $floorplan: MongoID, $warranty: MongoID, $additional: [MongoID], $front: [MongoID], $executor: Boolean, $aps: Boolean!) {
    savePdf(project: $project, unit: $unit, file: $file, purchaserIds: $purchaserIds, mergeTemplate: $mergeTemplate, floorplan: $floorplan, warranty: $warranty, additional: $additional, front: $front, executor: $executor, aps: $aps) {
      templateId
      createUrl
    }
  }
`;

export default withRouter(MergeStep);
